import { initialize as initializeFeed } from '@finn-no/feed';
import { debouncedLoadTags } from './podlet/ast';
import {
    Filter,
    HorseshoeConfig,
    KeywordsConfig,
} from '../shared/types/podlet';
import { getPodletConfigFromHtml } from './podlet/config';
import {
    verticalSpesificInit,
    verticalSpesificUpdate,
} from './podlet/vertical';
import { subscribe, publish } from './podlet/events';
import {
    Keywords,
    Apntag,
    PageType,
    Vertical,
    Subvertical,
} from '../shared/types/ast';
import { requestBanners } from './utils/banners';
import { renderHorseshoe } from './components/horseshoe-render';
import { renderMarketContent } from './components/market-content-render';
import { renderSkyscraper } from './components/skyscraper-render';
import { isEmpty } from '../shared/utils';

import { version } from '../shared/version';
import { filterKeywordsLegacy } from '../shared/filter';
import {
    filterKeywords,
    filterFeed,
    filterFeedString,
} from '../shared/url-lookup';
import { getGeoFilterLocationKeywords } from '../shared/url-lookup/geo-location-filter-handler';
import { verticalSpesificPreProccessing } from '../shared/appnexus/filter-pre-processing';

import { classifiedKeywords } from '../shared/search-query-classifier/search-query-classifier';
import diagnostics from './utils/diagnostics';
import {
    markBannerRefresh,
    markHorseshoeInitiated,
    markHorseshoeLoaded,
    markFilterUpdated,
    markPageLoaded,
    markSetPageOpts,
} from './utils/performance';

window.FINN = window.FINN || {};
window.FINN.banner = window.FINN.banner || {};
window.FINN.banner.queue = window.FINN.banner.queue || [];
window.FINN.banner.debouncedLoadTags =
    window.FINN.banner.debouncedLoadTags || debouncedLoadTags();

window.apntag = window.apntag || ({} as Apntag);
window.apntag.anq = window.apntag.anq || [];

let currentFilter: Filter | undefined;
let currentKeywordsConfig: Partial<KeywordsConfig> = {};
let currentFilterKeywords = {};
let currentPageType: PageType | undefined;
let currentSubvertical: Subvertical | undefined;
let keywords: Keywords;
let baseKeywords: Keywords | undefined;
let clientReady = false;
let cycle = 0;

function copyQueue() {
    const { queue } = window.FINN.banner;
    const tempQueue = Array.isArray(queue) ? [...queue] : [];

    if (window.apntag) {
        window.FINN.banner.queue = window.apntag.anq;
        const { anq } = window.apntag;
        tempQueue.forEach((element) => anq.push(element));
    }
}

function getFilterData(config: HorseshoeConfig) {
    const filterData = config.filter;
    if (filterData) {
        if (filterData.page) {
            filterData.page = Number(filterData.page);
        }
        if (filterData.side) {
            filterData.side = Number(filterData.side);
        }
    }
    return filterData;
}

/*
Legacy handling of command based event
 */
function updateKeywords(filter: Filter) {
    const vertical = keywords['no-sno-finn-section'];
    const update = filterKeywordsLegacy(vertical, filter, currentFilter);
    currentFilter = filter;

    if (update.keywords) {
        keywords = { ...baseKeywords, ...update.keywords };
        if (window.apntag && window.apntag.anq) {
            window.apntag.anq.push(() => {
                if (window.apntag) {
                    window.apntag.setPageOpts({
                        keywords,
                    });
                }
            });
        }
    }

    if (update.refresh) {
        if (window.apntag && window.apntag.anq) {
            window.apntag.anq.push(() => {
                if (window.apntag) {
                    window.apntag.refresh();
                }
            });
        }
    }
}

function diff(existing: {}, update: any) {
    return JSON.stringify(existing) !== JSON.stringify(update);
}

function pageChange(existingFilter?: Filter, updateFilter?: Filter) {
    if (updateFilter?.pageType) {
        return true;
    }
    return (
        existingFilter?.page !== updateFilter?.page ||
        existingFilter?.side !== updateFilter?.side
    );
}

function shouldRefresh(vertical: string, keywordDiff = false) {
    switch (vertical) {
        case 'car':
        case 'bap':
            return keywordDiff;
        default:
            return true;
    }
}

function updateFeed(filter: Filter) {
    const feed = filterFeed(filter, currentKeywordsConfig);
    const feedString = filterFeedString(filter, currentKeywordsConfig);

    window.FINN = window.FINN || {};
    window.FINN.banner = window.FINN.banner || {};
    window.FINN.banner.feed = feed;
    window.FINN.banner.feedString = feedString;
}

async function getAsyncKeywords(
    originalFilter: Filter,
    config: HorseshoeConfig,
) {
    if (!isEmpty(originalFilter)) {
        const { features, baseUrl } = config;
        const enableGeofilterLocationData =
            features?.find((i) => i.feature === 'enableGeofilterLocationData')
                ?.enabled || false;
        const enableSearchQueryClassification =
            features?.find(
                (i) => i.feature === 'enableSearchQueryClassification',
            )?.enabled || false;

        const geoFilterLocationKeywords = enableGeofilterLocationData
            ? await getGeoFilterLocationKeywords(originalFilter, baseUrl)
            : {};
        const keyWordsFromQuery = enableSearchQueryClassification
            ? await classifiedKeywords(
                  originalFilter as Filter,
                  config.vertical,
                  config.baseUrl,
              )
            : {};

        const [locationKeywords, searchKeywords] = await Promise.all([
            geoFilterLocationKeywords,
            keyWordsFromQuery,
        ]);
        return { ...locationKeywords, ...searchKeywords };
    } else return {};
}

function configUpdated(updatedConfig: Filter, config: HorseshoeConfig) {
    if (baseKeywords) {
        // Some travel subverticals don't know who they are before the client is loaded
        if (config.vertical && updatedConfig.subvertical) {
            baseKeywords[
                'no-sno-finn-subsection'
            ] = `${config.vertical}_${updatedConfig.subvertical}`;
            currentSubvertical = updatedConfig.subvertical as Subvertical;
        }
        if (updatedConfig.pageType) {
            baseKeywords['aa-sch-page_type'] = updatedConfig.pageType as string;
        }
    }
}

function refreshBanners() {
    if (clientReady) {
        cycle++;
        markBannerRefresh();
        publish('banner', 'refresh', cycle);
    } else {
        clientReady = true;
        copyQueue();
    }
}

function filterUpdated(originalFilter: Filter, config: HorseshoeConfig) {
    const vertical = keywords['no-sno-finn-section'] as Vertical;
    verticalSpesificUpdate(config, vertical, currentSubvertical);
    const filter = verticalSpesificPreProccessing(
        originalFilter,
        vertical,
        currentSubvertical,
    );

    const pageType = filter.pageType as PageType;

    if (pageType && pageType !== currentPageType) {
        filter['pagetype'] = [pageType];
        currentPageType = pageType;
    }

    const keywordsUpdate = filterKeywords(filter, currentKeywordsConfig);
    const keywordDiff = diff(currentFilterKeywords, keywordsUpdate);
    const alwaysRefresh = shouldRefresh(vertical, keywordDiff);
    const isPageChange = pageChange(currentFilter, filter);
    const refresh = alwaysRefresh || isPageChange;

    if (refresh) {
        if (window.apntag && window.apntag.anq) {
            getAsyncKeywords(originalFilter as Filter, config).then(
                (result) => {
                    currentFilter = filter;
                    currentFilterKeywords = keywordsUpdate;
                    keywords = {
                        ...baseKeywords,
                        ...keywordsUpdate,
                        ...result,
                    };
                    //@ts-ignore
                    window.apntag.anq.push(() => {
                        if (window.apntag) {
                            markSetPageOpts();
                            window.apntag.setPageOpts({
                                keywords,
                            });
                        }
                    });
                    refreshBanners();
                },
            );
        }
    }
    updateFeed(filter);
}

function loadConfig(config: HorseshoeConfig) {
    if (config.waitForClient) {
        clientReady = false;
    } else {
        clientReady = true;
    }

    const appNexusData = config.appNexus;
    currentSubvertical = config.subvertical;
    currentFilter = getFilterData(config);
    currentKeywordsConfig = config.keywordsConfig;
    currentFilterKeywords = filterKeywords(
        currentFilter,
        currentKeywordsConfig,
    );
    currentPageType = config.pageType;

    if (appNexusData && appNexusData.pageOpts) {
        baseKeywords = appNexusData.pageOpts.keywords;

        getAsyncKeywords(currentFilter as Filter, config).then((result) => {
            keywords = {
                ...baseKeywords,
                ...currentFilterKeywords,
                ...result,
            };
            appNexusData.pageOpts.keywords = keywords;

            const pageOpts = { ...appNexusData.pageOpts, ...keywords };
            const initialAppNexusData = { ...appNexusData, ...pageOpts };

            if (window.apntag) {
                window.apntag.anq.push(() => {
                    if (window.apntag) {
                        requestBanners(
                            window.apntag,
                            initialAppNexusData,
                            false,
                        );
                        if (
                            appNexusData.tags &&
                            Object.keys(appNexusData.tags).length
                        ) {
                            Object.values(appNexusData.tags).forEach(
                                window.apntag.defineTag,
                            );
                            debouncedLoadTags();
                        }
                    }
                    if (clientReady) {
                        copyQueue();
                    }
                });
            }

            subscribe('banner', 'update', (event) => {
                updateKeywords(event.payload as Filter);
            });

            subscribe('filter', 'updated', (event) => {
                markFilterUpdated();
                filterUpdated(event.payload as Filter, config);
            });

            subscribe('page', 'loaded', (event) => {
                markPageLoaded();
                configUpdated(event.payload as Filter, config);
                filterUpdated(event.payload as Filter, config);
            });

            initializeFeed();

            const props = {
                deviceType: config.deviceType,
                vertical: config.vertical,
                pageType: config.pageType,
            };

            if (!config.configOnly) {
                if (config.skyscraperOnly) {
                    renderSkyscraper('horseshoe-root', props);
                } else {
                    renderHorseshoe('horseshoe-root', props);
                }
            }

            if (config.loadContentboard) {
                // This usecase is only for the realestate market frontpage
                renderMarketContent('contentboard', props);
            }

            verticalSpesificInit(config);
        });
    }
}

function init() {
    markHorseshoeInitiated();
    const config = getPodletConfigFromHtml();

    if (config.version === version) {
        diagnostics.groupCollapsed(
            'displayads-podlet-horseshoe loading version: %s ',
            version,
        );
        diagnostics(config);
        diagnostics.groupEnd();
    } else {
        diagnostics(
            `Server version (${config.version}) does not match client version (${version})`,
        );
    }
    loadConfig(config);
}

markHorseshoeLoaded();
document.addEventListener('DOMContentLoaded', init);
